import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Avatar,
} from '@mui/material';
import {
  Agriculture,
  LocalHospital,
  TrendingUp,
  Forum,
  Chat,
  WbSunny,
  OpacitySharp as Water,
  Thermostat,
  Cloud,
  LocationOn,
  ShowChart,
  Timeline,
  Bolt,
  Grass,
  Map,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartTooltip,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config';
import { useTranslation } from 'react-i18next';

const baseStatHighlights = [
  {
    labelKey: 'dashboard.stats.items.success.label',
    fallbackLabel: 'Total predictions',
    value: '0',
    deltaKey: 'dashboard.stats.items.success.delta',
    fallbackDelta: 'Make a prediction to get started',
    icon: <ShowChart fontSize="small" />,
  },
  {
    labelKey: 'dashboard.stats.items.farms.label',
    fallbackLabel: 'Registered users',
    value: '0',
    deltaKey: 'dashboard.stats.items.farms.delta',
    fallbackDelta: 'Invite farmers to join',
    icon: <Grass fontSize="small" />,
  },
  {
    labelKey: 'dashboard.stats.items.risk.label',
    fallbackLabel: 'Disease scans',
    value: '0',
    deltaKey: 'dashboard.stats.items.risk.delta',
    fallbackDelta: 'From uploaded images',
    icon: <Bolt fontSize="small" />,
  },
];

const baseFeatureShortcuts = [
  {
    titleKey: 'dashboard.modules.cropPredictor.title',
    descriptionKey: 'dashboard.modules.cropPredictor.description',
    icon: <Agriculture fontSize="medium" />,
    path: '/dashboard/crop-predictor',
  },
  {
    titleKey: 'dashboard.modules.diseaseDetector.title',
    descriptionKey: 'dashboard.modules.diseaseDetector.description',
    icon: <LocalHospital fontSize="medium" />,
    path: '/dashboard/disease-detector',
  },
  {
    titleKey: 'dashboard.modules.financialDashboard.title',
    descriptionKey: 'dashboard.modules.financialDashboard.description',
    icon: <TrendingUp fontSize="medium" />,
    path: '/dashboard/financial-dashboard',
  },
  {
    titleKey: 'dashboard.modules.communityForum.title',
    descriptionKey: 'dashboard.modules.communityForum.description',
    icon: <Forum fontSize="medium" />,
    path: '/dashboard/community-forum',
  },
  {
    titleKey: 'dashboard.modules.chatbot.title',
    descriptionKey: 'dashboard.modules.chatbot.description',
    icon: <Chat fontSize="medium" />,
    path: '/dashboard/chatbot',
  },
];

const baseYieldTrendData: { monthKey: string; yield: number }[] = [];

const baseSoilHealthSignals: {
  titleKey: string;
  fallbackTitle: string;
  score: string;
  stateKey: string;
  fallbackState: string;
  tone: string;
}[] = [];

const baseQuickInsights = [
  { labelKey: 'dashboard.quickInsights.irrigation', icon: <Timeline fontSize="small" />, accent: 'primary.main' },
  { labelKey: 'dashboard.quickInsights.mandi', icon: <Map fontSize="small" />, accent: 'secondary.main' },
];

type WeatherErrorType = 'fetch' | 'denied' | 'unsupported';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const defaultStats = useMemo(
    () =>
      baseStatHighlights.map((stat) => ({
        label: t(stat.labelKey),
        value: stat.value,
        delta: t(stat.deltaKey),
        icon: stat.icon,
      })),
    [t]
  );

  const statLabelMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    baseStatHighlights.forEach((stat) => {
      map[stat.fallbackLabel] = t(stat.labelKey);
    });
    return map;
  }, [t]);

  const statDeltaMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    baseStatHighlights.forEach((stat) => {
      map[stat.fallbackDelta] = t(stat.deltaKey);
    });
    return map;
  }, [t]);

  const quickInsightChips = useMemo(
    () =>
      baseQuickInsights.map((insight) => ({
        ...insight,
        label: t(insight.labelKey),
      })),
    [t]
  );

  const defaultYieldData = useMemo(
    () => baseYieldTrendData.map((entry) => ({ month: t(`dashboard.months.${entry.monthKey}`), yield: entry.yield })),
    [t]
  );

  const defaultSoilSignals = useMemo(
    () =>
      baseSoilHealthSignals.map((signal) => ({
        title: t(signal.titleKey),
        score: signal.score,
        state: t(signal.stateKey),
        tone: signal.tone,
      })),
    [t]
  );

  const soilTitleMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    baseSoilHealthSignals.forEach((signal) => {
      map[signal.fallbackTitle] = t(signal.titleKey);
    });
    return map;
  }, [t]);

  const soilStateMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    baseSoilHealthSignals.forEach((signal) => {
      map[signal.fallbackState] = t(signal.stateKey);
    });
    return map;
  }, [t]);

  const featureShortcuts = useMemo(
    () =>
      baseFeatureShortcuts.map((feature) => ({
        title: t(feature.titleKey),
        description: t(feature.descriptionKey),
        icon: feature.icon,
        path: feature.path,
      })),
    [t]
  );

  const [weather, setWeather] = useState<{ temperature: number; condition?: string; humidity?: number; description?: string | null } | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [weatherError, setWeatherError] = useState<WeatherErrorType | null>(null);
  const [location, setLocation] = useState<string | null>(null);
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [dashboardStats, setDashboardStats] = useState<any[] | null>(null);
  const [yieldData, setYieldData] = useState<any[] | null>(null);
  const [soilSignals, setSoilSignals] = useState<any[] | null>(null);

  const fetchWeatherData = useCallback(async (lat: number, lon: number) => {
    try {
      setWeatherError(null);
      const response = await axios.get(`${API_BASE}/api/weather/current?lat=${lat}&lon=${lon}`);

      if (response.data.success) {
        const weatherData = response.data.current;
        const locationData = response.data.location;
        setWeather({
          temperature: Math.round(weatherData.temperature),
          condition: weatherData.main,
          humidity: weatherData.humidity,
          description: weatherData.description || null,
        });
        setLocation(locationData.name || null);
      } else {
        throw new Error(response.data.error || 'Failed to fetch weather');
      }
    } catch (error) {
      console.error('Weather fetch error:', error);
      setWeatherError('fetch');
      setLocation(null);
      setWeather({
        temperature: 22,
        condition: 'Sunny',
        humidity: 65,
        description: null,
      });
    } finally {
      setWeatherLoading(false);
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      // Fetch real-time dashboard stats
      const statsResponse = await axios.get(`${API_BASE}/api/dashboard/stats`);
      if (statsResponse.data.success && statsResponse.data.stats) {
        setDashboardStats(statsResponse.data.stats);
      }

      // Fetch yield trends
      const yieldResponse = await axios.get(`${API_BASE}/api/dashboard/yield-trends`);
      if (yieldResponse.data.success && yieldResponse.data.trends) {
        setYieldData(yieldResponse.data.trends);
      }

      // Fetch soil health signals
      const soilResponse = await axios.get(`${API_BASE}/api/dashboard/soil-health`);
      if (soilResponse.data.success && soilResponse.data.signals) {
        setSoilSignals(soilResponse.data.signals);
      }
    } catch (error) {
      console.log('Using fallback dashboard data:', error);
      // Keep using hardcoded fallbacks if API fails
    }
  }, []);

  const getUserLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCoords({ lat: latitude, lon: longitude });
          fetchWeatherData(latitude, longitude);
        },
        (error) => {
          console.error('Error getting location:', error);
          setWeatherError('denied');
          setLocation(null);
          setWeatherLoading(false);
          setWeather({
            temperature: 22,
            condition: 'Sunny',
            humidity: 65,
            description: null,
          });
        }
      );
    } else {
      setWeatherError('unsupported');
      setLocation(null);
      setWeatherLoading(false);
    }
  }, [fetchWeatherData]);

  // Fetch Mapbox token from backend config
  useEffect(() => {
    const fetchMapboxToken = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/config`);
        if (response.data?.mapboxToken) {
          setMapboxToken(response.data.mapboxToken);
        }
      } catch (err) {
        console.warn('Failed to fetch mapbox token:', err);
      }
    };
    fetchMapboxToken();
  }, []);

  // Initialize Mapbox GL map as a background and center on `coords` when available
  useEffect(() => {
    if (!mapboxToken || !mapContainerRef.current || mapRef.current || !coords) return;

    try {
      mapboxgl.accessToken = mapboxToken;

      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/satellite-streets-v12',
        center: [coords.lon, coords.lat],
        zoom: 13,
        interactive: false,
        attributionControl: false,
      });

      mapRef.current = map;

      // Add a subtle marker at user location
      new mapboxgl.Marker({ color: '#7DDF92' })
        .setLngLat([coords.lon, coords.lat])
        .addTo(map);

      // Dim slightly so the gradient scrim + text stay readable
      map.on('load', () => {
        const canvas = map.getCanvas();
        canvas.style.filter = 'brightness(0.6) saturate(1.2)';
      });
    } catch (e) {
      console.warn('Failed to initialize mapbox-gl:', e);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [mapboxToken, coords]);

  useEffect(() => {
    getUserLocation();
    fetchDashboardData();
  }, [getUserLocation, fetchDashboardData]);

  const statsToDisplay = useMemo(() => {
    if (dashboardStats && dashboardStats.length > 0) {
      return dashboardStats.map((stat, index) => {
        const baseIndex = index % defaultStats.length;
        const base = defaultStats[baseIndex];
        const baseKeys = baseStatHighlights[baseIndex];
        const label = stat.labelKey
          ? t(stat.labelKey)
          : typeof stat.label === 'string'
          ? statLabelMap[stat.label] || stat.label
          : base.label;
        const delta = stat.deltaKey
          ? t(stat.deltaKey)
          : typeof stat.delta === 'string'
          ? statDeltaMap[stat.delta] || stat.delta
          : base.delta;
        return {
          ...base,
          ...stat,
          label,
          delta,
          icon: stat.icon || base.icon || baseKeys.icon,
          value: stat.value || base.value,
        };
      });
    }
    return defaultStats;
  }, [dashboardStats, defaultStats, statLabelMap, statDeltaMap, t]);

  const soilSignalItems = useMemo(() => {
    if (soilSignals && soilSignals.length > 0) {
      return soilSignals.map((signal, index) => {
        const baseIndex = index % defaultSoilSignals.length;
        const base = defaultSoilSignals[baseIndex];
        const title = signal.titleKey
          ? t(signal.titleKey)
          : soilTitleMap[signal.title as keyof typeof soilTitleMap] || signal.title || base.title;
        const state = signal.stateKey
          ? t(signal.stateKey)
          : soilStateMap[signal.state as keyof typeof soilStateMap] || signal.state || base.state;
        return {
          ...base,
          ...signal,
          title,
          state,
          score: signal.score || base.score,
          tone: signal.tone || base.tone,
        };
      });
    }
    return defaultSoilSignals;
  }, [soilSignals, defaultSoilSignals, soilTitleMap, soilStateMap, t]);

  const yieldChartData = useMemo(
    () => (yieldData && yieldData.length > 0 ? yieldData : defaultYieldData),
    [yieldData, defaultYieldData]
  );

  const weatherErrorMessage = weatherError ? t(`dashboard.weather.errors.${weatherError}`) : null;
  const locationLabel = location ?? t('dashboard.weather.locationFallback');
  const weatherDescription = weather?.description ?? t('dashboard.weather.descriptionFallback');
  const humidityLabel =
    weather && typeof weather.humidity === 'number'
      ? t('dashboard.weather.humidity', { value: weather.humidity })
      : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Card
        className="glass-card"
        sx={{
          overflow: 'hidden',
          position: 'relative',
          borderRadius: 4,
          minHeight: { xs: 420, md: 370 },
        }}
      >
        {/* ── Mapbox map as the full card background ── */}
        <Box
          ref={mapContainerRef}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            '& canvas': { borderRadius: 'inherit' },
          }}
        />

        {/* ── Gradient scrim so text stays readable on the map ── */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
            background:
              'linear-gradient(135deg, rgba(10,15,12,0.82) 0%, rgba(10,15,12,0.55) 50%, rgba(10,15,12,0.30) 100%)',
            borderRadius: 'inherit',
          }}
        />

        {/* ── Card content overlaying the map ── */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            p: { xs: 3, md: 4 },
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            justifyContent: 'space-between',
            gap: { xs: 3, lg: 4 },
          }}
        >
          {/* Left: hero text + CTA */}
          <Box sx={{ flex: 1.2 }}>
            <Chip
              label={t('dashboard.hero.badge')}
              size="small"
              icon={<ShowChart fontSize="small" />}
              sx={{
                mb: 2,
                borderRadius: '999px',
                backgroundColor: 'rgba(125, 223, 146, 0.18)',
                color: '#fff',
                backdropFilter: 'blur(4px)',
                '.MuiChip-icon': { color: '#7DDF92' },
              }}
            />
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#fff', textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}>
              {t('dashboard.hero.title')}
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 520, mb: 3, color: 'rgba(255,255,255,0.78)' }}>
              {t('dashboard.hero.subtitle')}
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {quickInsightChips.map((insight, index) => (
                <Chip
                  key={index}
                  icon={insight.icon}
                  label={insight.label}
                  sx={{
                    borderRadius: '999px',
                    backgroundColor: 'rgba(125, 223, 146, 0.16)',
                    backdropFilter: 'blur(4px)',
                    color: '#fff',
                    '.MuiChip-icon': { color: insight.accent },
                  }}
                />
              ))}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/dashboard/crop-predictor')}
              >
                {t('dashboard.hero.primaryCta')}
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{ borderColor: 'rgba(255,255,255,0.4)', color: '#fff', '&:hover': { borderColor: '#7DDF92', background: 'rgba(125,223,146,0.08)' } }}
                onClick={() => navigate('/dashboard/chatbot')}
              >
                {t('dashboard.hero.secondaryCta')}
              </Button>
            </Stack>
          </Box>

          {/* Right: weather snapshot – frosted glass panel on top of map */}
          <Box
            sx={{
              flex: { lg: 0.55 },
              alignSelf: { lg: 'flex-start' },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              background: 'rgba(10, 14, 12, 0.55)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              borderRadius: 3,
              border: '1px solid rgba(125, 223, 146, 0.22)',
              p: 2.5,
              minWidth: { md: 260 },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#fff', letterSpacing: '0.04em' }}>
              {t('dashboard.weather.title')}
            </Typography>

            {weatherErrorMessage && (
              <Alert severity="warning" sx={{ borderRadius: 2, py: 0.5, fontSize: '0.75rem' }}>
                {weatherErrorMessage}
              </Alert>
            )}

            {weatherLoading ? (
              <Chip
                icon={<CircularProgress size={14} />}
                label={t('dashboard.weather.loading')}
                size="small"
                sx={{ alignSelf: 'flex-start', borderRadius: '999px' }}
              />
            ) : (
              <>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2.5,
                      background: 'linear-gradient(135deg, rgba(125,223,146,0.3) 0%, rgba(47,133,90,0.5) 100%)',
                      color: '#fff',
                    }}
                  >
                    {weather?.condition === 'Sunny' || weather?.condition === 'Clear' ? <WbSunny /> : <Cloud />}
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>
                      {weather?.temperature ?? 22}°C
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                      {weatherDescription}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={<LocationOn sx={{ fontSize: 14 }} />}
                    label={locationLabel}
                    size="small"
                    sx={{ borderRadius: '999px', fontSize: '0.7rem', backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', '.MuiChip-icon': { color: '#7DDF92' } }}
                  />
                  <Chip
                    icon={<Thermostat sx={{ fontSize: 14 }} />}
                    label={`${weather?.temperature ?? 22}°C`}
                    size="small"
                    sx={{ borderRadius: '999px', fontSize: '0.7rem', backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', '.MuiChip-icon': { color: '#7DDF92' } }}
                  />
                  {humidityLabel && (
                    <Chip
                      icon={<Water sx={{ fontSize: 14 }} />}
                      label={humidityLabel}
                      size="small"
                      sx={{ borderRadius: '999px', fontSize: '0.7rem', backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', '.MuiChip-icon': { color: '#7DDF92' } }}
                    />
                  )}
                </Stack>
              </>
            )}
          </Box>
        </Box>
      </Card>

      <Box className="layout-grid three">
        {statsToDisplay.length > 0 ? statsToDisplay.map((stat, index) => (
          <Card key={`${stat.label}-${index}`} className="surface-card">
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 16,
                    backgroundColor: 'rgba(125, 223, 146, 0.18)',
                    color: 'primary.main',
                  }}
                >
                  {stat.icon}
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1 }}>
                    {stat.label}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                    {stat.delta}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )) : (
          <Typography>{t('dashboard.stats.loading')}</Typography>
        )}
      </Box>

      <Box className="layout-grid two">
        <Card className="surface-card" sx={{ p: { xs: 2.5, md: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('dashboard.yield.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('dashboard.yield.description')}
          </Typography>
          {yieldChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={yieldChartData}>
                <defs>
                  <linearGradient id="yieldGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#7DDF92" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#7DDF92" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(125,223,146,0.25)" horizontal vertical={false} />
                <XAxis dataKey="month" stroke="var(--text-muted)" tickLine={false} axisLine={false} />
                <YAxis stroke="var(--text-muted)" tickLine={false} axisLine={false} width={40} />
                <RechartTooltip
                  cursor={{ stroke: 'rgba(125,223,146,0.35)', strokeWidth: 1 }}
                  contentStyle={{
                    borderRadius: 14,
                    border: '1px solid rgba(125,223,146,0.35)',
                    background: 'var(--bg-surface)',
                    boxShadow: 'var(--shadow-soft)',
                  }}
                />
                <Area type="monotone" dataKey="yield" stroke="#2f855a" fill="url(#yieldGradient)" strokeWidth={3} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260, flexDirection: 'column', gap: 1.5 }}>
              <Timeline sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                No prediction history yet.
              </Typography>
              <Button size="small" variant="outlined" onClick={() => navigate('/dashboard/crop-predictor')}>
                Make your first prediction
              </Button>
            </Box>
          )}
        </Card>

        <Card className="surface-card" sx={{ p: { xs: 2.5, md: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('dashboard.readiness.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('dashboard.readiness.description')}
          </Typography>
          <Stack spacing={2.2}>
            {soilSignalItems && soilSignalItems.length > 0 ? soilSignalItems.map((signal, index) => (
              <Box
                key={`${signal.title}-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderRadius: 3,
                  border: '1px solid var(--border-soft)',
                  backgroundColor: 'var(--bg-surface-subtle)',
                  px: 2,
                  py: 1.5,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {signal.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {signal.state}
                  </Typography>
                </Box>
                <Chip label={signal.score} color={signal.tone as any} variant="outlined" sx={{ borderRadius: '999px' }} />
              </Box>
            )) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1.5, py: 4 }}>
                <Grass sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 280 }}>
                  No sensor data available. Connect IoT soil probes for live field readiness signals.
                </Typography>
              </Box>
            )}
          </Stack>
        </Card>
      </Box>

      <Card className="surface-card" sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t('dashboard.modules.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('dashboard.modules.description')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: 2 }}>
            {featureShortcuts.map((feature) => (
              <Box
                key={feature.title}
                onClick={() => navigate(feature.path)}
                sx={{
                  cursor: 'pointer',
                  flex: '1 1 240px',
                  minWidth: 220,
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid var(--border-soft)',
                  backgroundColor: 'var(--bg-surface-subtle)',
                  display: 'flex',
                  gap: 1.5,
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 20px 45px rgba(31, 64, 45, 0.15)',
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    backgroundColor: 'rgba(125, 223, 146, 0.16)',
                    color: 'primary.main',
                  }}
                >
                  {feature.icon}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Card>
    </Box>
  );
};

export default Dashboard;